import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import type { NavigationItem } from '../types';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const navigationItems: NavigationItem[] = [
    { label: 'Inicio', href: '#' },
    { 
      label: 'Proyectos', 
      href: '#proyectos-destacados',
      submenu: [
        { label: 'Editorial', href: '#proyectos-destacados?filter=editoriales' },
        { label: 'Colaboraciones', href: '#proyectos-destacados?filter=colaboraciones' },
        { label: 'Videoclips', href: '#proyectos-destacados?filter=videoclips' },
        { label: 'Publi', href: '#proyectos-destacados?filter=publi' }
      ]
    },
    { label: 'Contacto', href: '#contacto' }
  ];

  // Función para bloquear/desbloquear el menú (evitar clicks múltiples)
  const lockMenu = useCallback((): boolean => {
    if (isLocked) return false;
    setIsLocked(true);
    setTimeout(() => setIsLocked(false), 350);
    return true;
  }, [isLocked]);

  // Ocultar menú sin hacer scroll
  const hideMenuOnly = useCallback(() => {
    if (lockMenu()) {
      setIsMenuOpen(false);
      setActiveSubmenu(null);
      document.body.classList.remove('is-menu-visible');
      
      // Restaurar la posición exacta guardada
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.left = '';
      
      // Restaurar inmediatamente a la posición guardada sin animación
      // Usar requestAnimationFrame para asegurar que se aplique después del cambio de estilos
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    }
  }, [lockMenu, scrollPosition]);

  // Ocultar menú con navegación (solo para cuando se selecciona una opción)
  const hideMenu = useCallback(() => {
    if (lockMenu()) {
      setIsMenuOpen(false);
      setActiveSubmenu(null);
      document.body.classList.remove('is-menu-visible');
      
      // Restaurar la posición del scroll solo cuando navegamos
      setTimeout(() => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  }, [lockMenu, scrollPosition]);

  // Mostrar menú
  const showMenu = () => {
    if (lockMenu()) {
      // Guardar la posición actual del scroll ANTES de hacer cualquier cambio
      const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      setScrollPosition(currentScrollPosition);
      
      // Fijar el body y aplicar el scroll como top negativo
      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      
      setIsMenuOpen(true);
      document.body.classList.add('is-menu-visible');
    }
  };

  // Toggle menú
  const toggleMenu = () => {
    if (lockMenu()) {
      if (isMenuOpen) {
        hideMenuOnly(); // Usar hideMenuOnly para cerrar sin navegación
      } else {
        showMenu();
      }
    }
  };

  // Toggle submenu
  const toggleSubmenu = (label: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Solo abrir/cerrar el submenu, sin navegar
    setActiveSubmenu(activeSubmenu === label ? null : label);
  };

  // Manejar click en enlaces
  const handleLinkClick = (href: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Cerrar el menú sin navegación primero
    hideMenuOnly();
    
    // Si es un enlace interno (ancla), hacer scroll suave
    if (href.startsWith('#') && href !== '#') {
      setTimeout(() => {
        // Verificar si hay parámetros de filtro
        const [targetId, filterParam] = href.split('?');
        const sectionId = targetId.substring(1); // Remover el #
        const element = document.getElementById(sectionId);
        
        if (element) {
          // Restaurar posición del scroll antes de navegar
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          
          // Calcular la altura real del header
          const header = document.getElementById('header');
          const headerHeight = header ? header.offsetHeight + 20 : 100;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          
          window.scrollTo({
            top: elementPosition - headerHeight,
            behavior: 'smooth'
          });
          
          // Si hay parámetro de filtro, activarlo
          if (filterParam && filterParam.startsWith('filter=')) {
            const filterValue = filterParam.split('=')[1];
            // Disparar evento personalizado para cambiar el filtro
            setTimeout(() => {
              const filterEvent = new CustomEvent('changeProjectFilter', { 
                detail: { filter: filterValue } 
              });
              window.dispatchEvent(filterEvent);
            }, 500); // Esperar a que termine el scroll
          }
        } else {
          // Si no encuentra el elemento, probablemente estamos en otra página
          // Navegar al home con React Router
          if (location.pathname !== '/') {
            // Estamos en otra página, navegar al home
            if (filterParam && filterParam.startsWith('filter=')) {
              const filterValue = filterParam.split('=')[1];
              navigate('/', { 
                state: { 
                  filter: filterValue,
                  shouldShowAll: false
                }
              });
            } else {
              navigate('/');
            }
          } else {
            console.log(`Element with id "${sectionId}" not found`);
          }
        }
      }, 300);
    } else if (href === '#') {
      // Scroll al top o navegar al home
      if (location.pathname === '/') {
        // Estamos en home, hacer scroll al top
        setTimeout(() => {
          // Restaurar posición del scroll antes de navegar
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }, 300);
      } else {
        // Estamos en otra página, navegar al home
        navigate('/');
      }
    }
  };

  // Manejar tecla ESC y clicks fuera del menú
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideMenuOnly(); // Sin navegación al cerrar con ESC
      }
    };

    const handleBodyClick = (event: Event) => {
      // Solo ocultar si se hace click directamente en el fondo del menú
      const target = event.target as HTMLElement;
      if (target.id === 'menu') {
        hideMenuOnly(); // Sin navegación al cerrar con click fuera
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleBodyClick);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleBodyClick);
    };
  }, [isMenuOpen, hideMenuOnly]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      // Limpiar estilos del body al desmontar el componente
      document.body.classList.remove('is-menu-visible');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, []);

  return (
    <>
      <nav>
        <a
          href="#menu"
          className="navigation-menu-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleMenu();
          }}
        >
          Menu
        </a>
      </nav>

      <nav 
        id="menu" 
        className={isMenuOpen ? 'visible' : ''}
        onClick={(e) => {
          // Cerrar menú si se hace click en el fondo
          if (e.target === e.currentTarget) {
            hideMenuOnly(); // Sin navegación al cerrar con click en fondo
          }
        }}
      >
        <div className="inner">
          <a 
            className="close" 
            href="#menu"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              hideMenuOnly(); // Sin navegación al cerrar con X
            }}
          >
            ✕
          </a>
          <ul className="links">
            {navigationItems.map((item) => (
              <li key={item.label} className={item.submenu ? 'has-submenu' : ''}>
                {item.submenu ? (
                  <>
                    <a
                      href={item.href}
                      onClick={(e) => toggleSubmenu(item.label, e)}
                      className={activeSubmenu === item.label ? 'active' : ''}
                    >
                      {item.label}
                    </a>
                    <ul className={`submenu ${activeSubmenu === item.label ? 'active' : ''}`}>
                      {item.submenu.map((subItem) => (
                        <li key={subItem.label}>
                          <a 
                            href={subItem.href}
                            onClick={(e) => handleLinkClick(subItem.href, e)}
                          >
                            {subItem.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  item.href.startsWith('#') ? (
                    <a 
                      href={item.href}
                      onClick={(e) => handleLinkClick(item.href, e)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link 
                      to={item.href}
                      onClick={(e) => {
                        e.stopPropagation();
                        hideMenuOnly(); // Solo cerrar menú, sin scroll
                      }}
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </li>
            ))}
          </ul>
          <ul className="actions stacked">
            <li>
              <a 
                href="#proyectos-destacados" 
                className="button primary fit"
                onClick={(e) => handleLinkClick('#proyectos-destacados?filter=todos', e)}
              >
                Ver Proyectos
              </a>
            </li>
          </ul>
          <a 
            className="close" 
            href="#menu"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              hideMenu();
            }}
          >
            ✕
          </a>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
